import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'designSystem/components/Table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { readableMarkdown } from 'designSystem/readableMarkdown'
import { InfoIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import ReactMarkdown, { type Components, type Options } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import styled, { css } from 'styled-components'

const MarkdownRoot = styled.div<{ $preserveWhitespace: boolean }>`
  margin: 0;
  color: var(--foreground);
  font-size: inherit;
  font-weight: 400;
  line-height: inherit;
  overflow-wrap: anywhere;

  ${({ $preserveWhitespace }) =>
    $preserveWhitespace &&
    css`
      white-space: normal;

      p {
        white-space: pre-wrap;
      }
    `}

  p {
    margin: 0 0 0.5em;
    font-weight: 400;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    display: block;
    margin: 0.25em 0;
    padding-left: 1.5em;
    list-style-position: outside;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    display: list-item;
    margin: 0.15em 0;
    font-weight: 400;
  }

  li > p {
    margin: 0;
  }

  em {
    font-style: italic;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1em 0 0.4em;
    font-weight: 600;
    line-height: 1.25;
    color: var(--foreground);

    &:first-child {
      margin-top: 0;
    }
  }

  /* Same steps as the Text scale (title, heading, subheading) over a text-sm body. */
  h1 {
    font-size: 1.43em;
    letter-spacing: -0.025em;
  }

  h2 {
    font-size: 1.29em;
  }

  h3 {
    font-size: 1.14em;
  }

  h4 {
    font-size: 1em;
  }

  blockquote {
    margin: 0.5em 0;
    padding-left: 0.75em;
    border-left: 2px solid var(--border);
    color: var(--muted-foreground);
  }

  hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 0.75em 0;
  }

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
  }

  pre {
    margin: 0.5em 0;
    padding: 0.5em 0.75em;
    overflow-x: auto;
    border-radius: 0.25rem;
    background: var(--muted);
  }

  pre code {
    font-size: 0.9em;
  }
`

const Bold = styled.strong`
  color: var(--foreground);
  font-weight: 700;
`

const MarkdownLink = styled.a`
  color: inherit;
  text-decoration: underline;
`

const InfoHintButton = styled.button`
  display: inline-flex;
  margin: 0 0.15em;
  padding: 0;
  border: none;
  background: none;
  color: var(--muted-foreground);
  font: inherit;
  line-height: 1;
  vertical-align: -0.125em;
  cursor: help;
`

// The agent writes a hover hint as `[ⓘ](#info "hint")`; show it as an icon.
const INFO_HINT_HREF = '#info'

/** The hint text when a Markdown link is an `#info` hint, else `undefined`. */
export function infoHintText(
  href: string | undefined,
  title: string | undefined
): string | undefined {
  return href === INFO_HINT_HREF && title?.trim() ? title : undefined
}

/** Inline info icon that shows its hint in a tooltip on hover and focus. */
export function MarkdownInfoHint({
  hint,
}: {
  hint: string
}): React.ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <InfoHintButton type='button' aria-label={hint}>
          <InfoIcon size='1em' />
        </InfoHintButton>
      </TooltipTrigger>
      <TooltipContent width='measure' zIndex='overlay'>
        {hint}
      </TooltipContent>
    </Tooltip>
  )
}

const markdownComponents: Components = {
  strong: ({ children }) => <Bold>{children}</Bold>,
  b: ({ children }) => <Bold>{children}</Bold>,
  a: ({ href, title, children }) => {
    const hint = infoHintText(href, title)
    if (hint) return <MarkdownInfoHint hint={hint} />
    return (
      <MarkdownLink href={href} title={title}>
        {children}
      </MarkdownLink>
    )
  },
  table: ({ children }) => <Table layout='prose'>{children}</Table>,
  thead: ({ children }) => <TableHeader layout='prose'>{children}</TableHeader>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow layout='prose'>{children}</TableRow>,
  th: ({ children }) => (
    <TableHead layout='prose' size='compact'>
      {children}
    </TableHead>
  ),
  td: ({ children }) => (
    <TableCell layout='prose' size='compact'>
      {children}
    </TableCell>
  ),
}

/**
 * Current product-chat renderer kept at the shared design-system boundary
 * while product surfaces converge on the canonical Markdown component.
 * User-authored content skips model-output cleanup and preserves soft line breaks.
 * `remarkPlugins`/`components` are additive: callers that pass them get
 * their plugins appended after `remarkGfm` and their component overrides
 * layered on top of the base element map; existing callers that omit them
 * are unaffected.
 */
export function AgenticMarkdown({
  children,
  remarkPlugins,
  components,
  source = 'agent',
  className,
}: {
  children: string
  remarkPlugins?: Options['remarkPlugins']
  components?: Components
  source?: 'agent' | 'user'
  className?: string
}): React.ReactElement {
  const allRemarkPlugins = useMemo(
    () => [remarkGfm, ...(remarkPlugins ?? [])],
    [remarkPlugins]
  )
  const allComponents = useMemo(
    () => ({ ...markdownComponents, ...components }),
    [components]
  )
  const markdown = useMemo(
    () => (source === 'user' ? children : readableMarkdown(children)),
    [children, source]
  )
  return (
    <MarkdownRoot className={className} $preserveWhitespace={source === 'user'}>
      <ReactMarkdown
        remarkPlugins={allRemarkPlugins}
        components={allComponents}>
        {markdown}
      </ReactMarkdown>
    </MarkdownRoot>
  )
}
